import { createElement, type ComponentPropsWithRef, type ReactNode } from 'react';

type CardElement = 'div' | 'section' | 'form' | 'button';
type CardProps<T extends CardElement> = ComponentPropsWithRef<T> & { as?: T };

export default function Card<T extends CardElement = 'div'>({
  as,
  className,
  ...props
}: CardProps<T>) {
  return createElement(as ?? 'div', {
    ...props,
    className: `card${className ? ` ${className}` : ''}`,
  });
}

type CardHeaderProps = Omit<ComponentPropsWithRef<'div'>, 'title'> & {
  title?: ReactNode;
  actions?: ReactNode;
};

export function CardHeader({ title, actions, children, className, ...props }: CardHeaderProps) {
  const heading = title !== undefined ? <h3 className="text-lg font-medium">{title}</h3> : null;

  return (
    <div {...props} className={`card-header${className ? ` ${className}` : ''}`}>
      {actions !== undefined ? (
        <div className="flex items-center justify-between gap-3">
          {heading}
          {actions}
        </div>
      ) : heading}
      {children}
    </div>
  );
}

type CardBodyProps = ComponentPropsWithRef<'div'> & {
  padding?: 'default' | 'horizontal' | 'none';
};

export function CardBody({ padding = 'default', className, ...props }: CardBodyProps) {
  const paddingClass = {
    default: 'card-body',
    horizontal: 'card-body-horizontal',
    none: '',
  }[padding];

  return <div {...props} className={[paddingClass, className].filter(Boolean).join(' ') || undefined} />;
}
